import React, { useEffect, useMemo, useState } from "react"
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import clsx from "clsx"
import ChartEvolutionEffectifsMouvements from "./components/ChartEvolutionEffectifsMouvements"
import MyModal from "@common-utils/MyModal"
import ConsultantDetails from "../../../modules/business/consultants/details/ConsultantDetails" //

interface Props {
     initialData: { chartData: any[]; metadata: any } | null
}

/**
 * Composant interne pour la liste des consultants (Tooltip & Mode Verrouillé)
 */
const ConsultantDetailCard = ({
     data,
     onType,
     onClose,
     onConsultantClick,
}: {
     data: any
     onType: "tooltip" | "locked"
     onClose?: () => void
     onConsultantClick: (id: number, name: string) => void // Callback pour le modal
}) => (
     <div
          className={clsx("bg-white p-5 border shadow-sm rounded-3", onType === "locked" && "border-primary")}
          style={{
               minWidth: "250px",
               zIndex: 10000,
               pointerEvents: "auto",
          }}
     >
          <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2 ">
               <div className="fw-bolder fs-6 text-gray-800 text-capitalize">{data.displayName}</div>
               {onType === "locked" && (
                    <button className="btn btn-icon btn-sm btn-active-light-primary border-0" onClick={onClose}>
                         <i className="fas fa-times text-muted"></i>
                    </button>
               )}
          </div>

          {/* Entrées */}
          {data.entrees_details?.length > 0 && (
               <div className="mb-4">
                    <div className="text-success fw-boldest fs-9 text-uppercase mb-2 d-flex align-items-center">
                         <i className="fas fa-user-plus text-success me-2"></i>
                         {data.entrees} Entrée(s)
                    </div>
                    <div className="d-flex flex-column gap-1 ps-2">
                         {data.entrees_details.map((c: any) => (
                              <div
                                   key={c.id}
                                   onClick={() => onConsultantClick(parseInt(c.id), c.name)} // Ouvre le modal
                                   className="fs-9 text-gray-700 fw-bold d-flex align-items-center text-hover-primary cursor-pointer transition-all"
                              >
                                   <span className="bullet bullet-dot bg-success h-4px w-4px me-2"></span>
                                   {c.name}
                              </div>
                         ))}
                    </div>
               </div>
          )}

          {/* Sorties */}
          {data.sorties_details?.length > 0 && (
               <div className="mb-4">
                    <div className="text-danger fw-boldest fs-9 text-uppercase mb-2 d-flex align-items-center">
                         <i className="fas fa-user-minus text-danger me-2"></i>
                         {data.sorties} Sortie(s)
                    </div>
                    <div className="d-flex flex-column gap-1 ps-2">
                         {data.sorties_details.map((c: any) => (
                              <div
                                   key={c.id}
                                   onClick={() => onConsultantClick(parseInt(c.id), c.name)}
                                   className="fs-9 text-gray-700 fw-bold d-flex align-items-center text-hover-primary cursor-pointer transition-all"
                              >
                                   <span className="bullet bullet-dot bg-danger h-4px w-4px me-2"></span>
                                   {c.name}
                              </div>
                         ))}
                    </div>
               </div>
          )}

          <div className="separator separator-dashed my-3"></div>
          <div className="d-flex justify-content-between align-items-center">
               <span className="text-muted fs-8 fw-bold">Effectif Total :</span>
               <span className="badge badge-light-primary fw-bolder fs-7">{data.effectif}</span>
          </div>
     </div>
)

const ChartEvolutionsEffectifs = ({ initialData }: Props) => {
     const [rawData, setRawData] = useState<any[]>(initialData?.chartData || [])
     const [viewType, setViewType] = useState<"chart" | "list">(window.innerWidth < 992 ? "list" : "chart")
     const [mode, setMode] = useState("MENSUEL_OR_COMULE_OR_TOTAL__TOTAL")

     const [selectedPoint, setSelectedPoint] = useState<any | null>(null)
     const [lockedCoords, setLockedCoords] = useState<{ x: number; y: number } | null>(null)

     // État pour gérer le consultant affiché en plein écran
     const [modalConsultant, setModalConsultant] = useState<{ id: number; name: string } | null>(null)

     // Gestion de la fermeture par Échap
     useEffect(() => {
          const handleKeyDown = (e: KeyboardEvent) => {
               if (e.key === "Escape") {
                    setSelectedPoint(null)
                    setLockedCoords(null)
                    setModalConsultant(null)
               }
          }
          window.addEventListener("keydown", handleKeyDown)
          return () => window.removeEventListener("keydown", handleKeyDown)
     }, [])

     const formatMonthLabel = (name: string) => {
          if (!name || !name.includes("-")) return name
          const [year, month] = name.split("-")
          const date = new Date(parseInt(year), parseInt(month) - 1)
          const formatted = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(date)
          return formatted.charAt(0).toUpperCase() + formatted.slice(1)
     }

     const formatShortDate = (name: string) => {
          if (!name || !name.includes("-")) return name
          const [year, month] = name.split("-")
          return `${month}/${year.slice(-2)}`
     }

     const chartData = useMemo(() => {
          let runningNet = 0
          return rawData.map(item => {
               const monthlyNet = (item.entrees || 0) - (item.sorties || 0)
               runningNet += monthlyNet
               return {
                    ...item,
                    displayName: formatMonthLabel(item.name),
                    shortName: formatShortDate(item.name),
                    netChange: monthlyNet,
                    cumulatedNet: runningNet,
               }
          })
     }, [rawData])

     const yDomain = useMemo(() => {
          if (!chartData.length) return [0, "auto"] as [number, string]

          const key = mode === "MENSUEL_OR_COMULE_OR_TOTAL__TOTAL" ? "effectif" : mode === "MENSUEL_OR_COMULE_OR_TOTAL__MENSUEL" ? "netChange" : "cumulatedNet"
          const values = chartData.map(d => d[key] as number).filter(v => v !== undefined && v !== null)
          if (!values.length) return [0, "auto"] as [number, string]

          const min = Math.min(...values)
          const max = Math.max(...values)
          const padding = Math.max(Math.ceil((max - min) * 0.2), 1)

          return [min - padding, max + padding] as [number, number]
     }, [chartData, mode])

     const handleChartClick = (state: any) => {
          if (state && state.activeTooltipIndex !== undefined && state.activeCoordinate) {
               setSelectedPoint(chartData[state.activeTooltipIndex])
               setLockedCoords({
                    x: state.activeCoordinate.x,
                    y: state.activeCoordinate.y,
               })
          }
     }

     useEffect(() => {
          if (initialData?.chartData) setRawData(initialData.chartData)
     }, [initialData])

     return (
          <div className="d-flex flex-column h-100 position-relative">
               <div className="mb-6">
                    <h3 className="fw-bolder text-gray-800 m-0 fs-2">Évolution des effectifs</h3>
               </div>

               <div className="d-flex flex-column flex-lg-row justify-content-between align-items-stretch align-items-lg-center gap-4 mb-8">
                    <div className="d-flex align-items-center gap-3 flex-wrap flex-grow-1">
                         <div className="nav-group nav-group-outline bg-light rounded p-0 d-none d-lg-flex">
                              <button
                                   className={clsx("btn btn-icon btn-sm border-0", viewType === "chart" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                                   onClick={() => setViewType("chart")}
                              >
                                   <i className="fas fa-chart-line fs-6"></i>
                              </button>
                              <button
                                   className={clsx("btn btn-icon btn-sm border-0", viewType === "list" ? "bg-white shadow-sm text-primary" : "text-gray-500")}
                                   onClick={() => setViewType("list")}
                              >
                                   <i className="fas fa-list fs-6"></i>
                              </button>
                         </div>

                         <div className="flex-grow-1 flex-lg-grow-0 min-w-lg-350px">
                              <ChartEvolutionEffectifsMouvements
                                   entreesIds={initialData?.metadata?.entrees_total_ids || []}
                                   sortiesIds={initialData?.metadata?.sorties_total_ids || []}
                              />
                         </div>
                    </div>

                    <div className="nav-group nav-group-outline bg-light rounded p-0 d-flex flex-grow-1 flex-lg-grow-0">
                         {["TOTAL", "MENSUEL", "CUMULE"].map(m => (
                              <button
                                   key={m}
                                   className={clsx(
                                        "btn btn-sm px-6 fw-bold flex-grow-1 border-0",
                                        mode === `MENSUEL_OR_COMULE_OR_TOTAL__${m}` ? "bg-white shadow-sm text-primary" : "text-gray-500"
                                   )}
                                   onClick={() => setMode(`MENSUEL_OR_COMULE_OR_TOTAL__${m}`)}
                              >
                                   {m === "TOTAL" ? "Effectif" : m === "MENSUEL" ? "Mensuel" : "Cumulé"}
                              </button>
                         ))}
                    </div>
               </div>

               <div className="flex-grow-1 w-100 position-relative">
                    {viewType === "chart" ? (
                         <>
                              <div style={{ height: "450px" }}>
                                   <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart
                                             data={chartData}
                                             margin={{ top: 10, right: 10, bottom: 20, left: 0 }}
                                             onClick={handleChartClick}
                                             style={{ cursor: "pointer" }}
                                        >
                                             <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                             <XAxis dataKey="shortName" fontSize={11} tickLine={false} axisLine={false} tick={{ fill: "#7E8299" }} dy={10} />
                                             <YAxis fontSize={11} axisLine={false} tickLine={false} domain={yDomain} />

                                             <Tooltip
                                                  content={({ active, payload }) =>
                                                       active && payload && !selectedPoint ? (
                                                            <ConsultantDetailCard
                                                                 data={payload[0].payload}
                                                                 onType="tooltip"
                                                                 onConsultantClick={(id, name) => setModalConsultant({ id, name })} //
                                                            />
                                                       ) : null
                                                  }
                                                  cursor={{ fill: "#F5F8FA" }}
                                                  wrapperStyle={{ zIndex: 10000, pointerEvents: "none" }}
                                             />

                                             <Legend verticalAlign="bottom" align="center" iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />

                                             {mode !== "MENSUEL_OR_COMULE_OR_TOTAL__TOTAL" && (
                                                  <>
                                                       <Bar name="Entrées" dataKey="entrees" fill="#34bfa3" radius={[4, 4, 0, 0]} barSize={20} />
                                                       <Bar name="Sorties" dataKey="sorties" fill="#f4516c" radius={[4, 4, 0, 0]} barSize={20} />
                                                       <ReferenceLine y={0} stroke="#E4E6EF" />
                                                  </>
                                             )}
                                             <Line
                                                  name={mode === "MENSUEL_OR_COMULE_OR_TOTAL__TOTAL" ? "Effectif Total" : "Évolution"}
                                                  type="monotone"
                                                  dataKey={
                                                       mode === "MENSUEL_OR_COMULE_OR_TOTAL__TOTAL"
                                                            ? "effectif"
                                                            : mode === "MENSUEL_OR_COMULE_OR_TOTAL__MENSUEL"
                                                            ? "netChange"
                                                            : "cumulatedNet"
                                                  }
                                                  stroke={mode === "MENSUEL_OR_COMULE_OR_TOTAL__MENSUEL" ? "#7239ea" : "#36a3f7"}
                                                  strokeWidth={4}
                                                  dot={{ r: 5, fill: "#fff", strokeWidth: 3, stroke: mode === "MENSUEL_OR_COMULE_OR_TOTAL__MENSUEL" ? "#7239ea" : "#36a3f7" }}
                                             />
                                        </ComposedChart>
                                   </ResponsiveContainer>
                              </div>

                              {/* TOOLTIP VERROUILLÉ : CENTRÉ VERTICALEMENT */}
                              {selectedPoint && lockedCoords && (
                                   <div
                                        className="position-absolute shadow-lg"
                                        style={{
                                             left: `${lockedCoords.x}px`,
                                             top: "50%",
                                             transform: "translate(-50%, -50%)",
                                             zIndex: 11000,
                                             transition: "left 0.2s ease-out",
                                        }}
                                   >
                                        <ConsultantDetailCard
                                             data={selectedPoint}
                                             onType="locked"
                                             onConsultantClick={(id, name) => setModalConsultant({ id, name })} //
                                             onClose={() => {
                                                  setSelectedPoint(null)
                                                  setLockedCoords(null)
                                             }}
                                        />
                                   </div>
                              )}
                         </>
                    ) : (
                         <div className="row g-5 overflow-auto p-2" style={{ maxHeight: "600px" }}>
                              {chartData.map((item, idx) => (
                                   <div key={idx} className="col-12 col-md-6 col-xl-4">
                                        <ConsultantDetailCard
                                             data={item}
                                             onType="tooltip"
                                             onConsultantClick={(id, name) => setModalConsultant({ id, name })} //
                                        />
                                   </div>
                              ))}
                         </div>
                    )}
               </div>

               {/* MODAL FULLSCREEN POUR LES DÉTAILS */}
               <MyModal
                    title={modalConsultant?.name} // Nom dans l'en-tête
                    show={modalConsultant !== null}
                    onCloseCallback={() => setModalConsultant(null)}
                    fullscreen // Mode plein écran
               >
                    {modalConsultant !== null && <ConsultantDetails idConsultantProp={modalConsultant.id} />}
               </MyModal>
          </div>
     )
}

export default ChartEvolutionsEffectifs
